import { useState, useRef, useEffect, useMemo, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Search, X, Loader2 } from 'lucide-react';
import { useFulltextSearch } from '../../hooks/useFulltextSearch';
import HighlightedText from '../common/HighlightedText';
import { useTranslation } from '../../i18n';

interface NavbarSearchProps {
  onNavigate?: () => void;
}

export default function NavbarSearch({ onNavigate }: NavbarSearchProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();
  const trimmed = query.trim();

  const { results, isLoading } = useFulltextSearch(trimmed.length < 2 ? '' : trimmed, 'quick');

  const visibleResults = useMemo(() => results.slice(0, 8), [results]);

  const showDropdown = open && trimmed.length >= 2;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  const [lastPathname, setLastPathname] = useState(location.pathname);
  if (location.pathname !== lastPathname) {
    setLastPathname(location.pathname);
    setOpen(false);
  }

  const showAllResults = () => {
    navigate(`/products?name=${encodeURIComponent(trimmed)}`);
    setOpen(false);
    setQuery('');
    onNavigate?.();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    // Enter opens the highlighted result, otherwise it filters the product list
    if (showDropdown && activeIndex >= 0 && visibleResults[activeIndex]) {
      handleProductClick(visibleResults[activeIndex].id);
      return;
    }
    if (trimmed) {
      showAllResults();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || visibleResults.length === 0) {
      if (e.key === 'Escape') {
        setOpen(false);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, visibleResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleProductClick = (id: number) => {
    setOpen(false);
    setQuery('');
    navigate(`/products/${id}`, { state: { backgroundLocation: location, searchTerm: trimmed } });
    onNavigate?.();
  };

  return (
    <div ref={wrapperRef} className="relative">
      <form onSubmit={handleSubmit} className="flex items-center relative">
        <Search size={15} className="absolute left-2.5 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t.nav.searchPlaceholder}
          aria-label={t.nav.searchPlaceholder}
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls="navbar-search-results"
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? `navbar-search-result-${activeIndex}` : undefined}
          className="w-36 xl:w-44 2xl:w-52 pl-8 pr-8 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-vivatis-500 focus:border-transparent transition-colors"
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(''); setOpen(false); }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded cursor-pointer flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <X size={14} />
          </button>
        )}
      </form>

      {showDropdown && (
        <div className="absolute right-0 z-50 mt-1 w-[min(22rem,calc(100vw-2rem))] max-h-96 overflow-y-auto rounded-xl bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 text-vivatis-400 animate-spin" />
            </div>
          ) : results.length === 0 ? (
            <div
              role="status"
              aria-live="polite"
              className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400 break-words"
            >
              {t.common.noResultsFor(trimmed)}
            </div>
          ) : (
            <>
              <div id="navbar-search-results" role="listbox" aria-label={t.nav.searchPlaceholder}>
                {visibleResults.map((product, i) => (
                  <button
                    key={product.id}
                    type="button"
                    id={`navbar-search-result-${i}`}
                    role="option"
                    aria-selected={i === activeIndex}
                    onClick={() => handleProductClick(product.id)}
                    className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors cursor-pointer ${
                      i === activeIndex
                        ? 'bg-gray-100 dark:bg-gray-700'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {product.logoUrl ? (
                      <img
                        src={product.logoUrl}
                        alt=""
                        className="w-8 h-8 rounded object-contain bg-gray-100 dark:bg-gray-700 shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded bg-gray-100 dark:bg-gray-700 shrink-0 flex items-center justify-center">
                        <span className="text-[10px] text-gray-400 font-bold">
                          {product.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        <HighlightedText text={product.name} term={trimmed} />
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        <HighlightedText text={product.partner?.name} term={trimmed} />
                        {product.partner?.name && product.articleNumber ? ' · ' : ''}
                        <HighlightedText text={product.articleNumber} term={trimmed} />
                      </p>
                    </div>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={showAllResults}
                className="w-full text-center px-3 py-2.5 text-sm text-vivatis-600 dark:text-vivatis-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer border-t border-gray-200 dark:border-gray-700"
              >
                {t.home.actions.viewAllProducts(results.length)}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
